import { ChangeEvent, useEffect, useRef, useState } from "react";
import { USER } from "@types/user";
import LoadingIcon from "@common/LoadingIcon";
import { error_message } from "@constants/Errors";
import messages from "@constants/Messages";
import WarningModal from "@components/Modals/WarningModal";

type UserImageTableProps = {
  selectedUsers: [];
  setSelectedUsers: (data: []) => void;
  setFileMap: (data: []) => void;
  userInformation: USER;
};

const UserImageTable = ({
  selectedUsers,
  setSelectedUsers,
  setFileMap,
  userInformation,
}: UserImageTableProps) => {
  const [userData, setUserData] = useState<USER[]>([]);
  const [imageLoading, setImageLoading] = useState<boolean>(false);

  const fileInputs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const [previewsMap, setPreviewsMap] = useState<{ [key: number]: string[] }>(
    {},
  );

  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);

  const currentUsers: USER[] = userData;

  useEffect(() => {
    setUserData(userInformation);
  }, [userInformation]);

  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
    };

    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  const handleUserSelect = (userId: number, force = false) => {
    setSelectedUsers((prevSelected) => {
      if (force) {
        return [...prevSelected, userId];
      } else if (prevSelected.includes(userId)) {
        return prevSelected.filter((id) => id !== userId);
      } else {
        return [...prevSelected, userId];
      }
    });
  };

  const handleFileChange = (
    userId: number,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    setFileMap((prevFileMap) => ({
      ...prevFileMap,
      [userId]: selectedFiles,
    }));

    const filePreviews = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviewsMap((prevPreviewsMap) => ({
      ...prevPreviewsMap,
      [userId]: filePreviews,
    }));

    handleUserSelect(userId, true);
  };

  const handleDrop = (e, user) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && /\.(png|jpe?g)$/i.test(droppedFile.name)) {
      const syntheticEvent = {
        target: { files: [droppedFile] },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(user.id, syntheticEvent);
    } else {
      setWarningModalOpen(true);
    }
  };

  return (
    <div className="w-full">
      <div className="border border-text-field-border bg-white rounded-2xl overflow-hidden">
        <div className="overflow-x-auto overflow-y-hidden mb-2">
          <table className="min-w-[950px] w-full table-fixed mayo-card-header rounded-2xl">
            <thead className="border-b border-text-field-border">
              <tr>
                <th className="py-4 text-left label-light-blue-xl min-w-[50px] w-15"></th>
                <th className="py-4 text-left label-light-blue-xl min-w-[50px] w-10">
                  氏名
                </th>
                <th className="py-4 text-left label-light-blue-xl min-w-[50px] w-10">
                  ユーザーID
                </th>
              </tr>
            </thead>
            <tbody className="mayo-card-body rounded-2xl">
              {currentUsers?.length === 0 ? (
                <tr>
                  <td></td>
                  <td colSpan="5" className="text-center py-4">
                    {error_message.user.no_data}
                  </td>
                </tr>
              ) : (
                currentUsers?.map((user) => (
                  <tr key={user.id}>
                    <td className="py-5 flex items-center gap-2.5 min-w-[20px]">
                      <div className="relative w-24 h-24">
                        {selectedUsers?.profile_image_url && imageLoading ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingIcon
                              width={8}
                              height={8}
                              textSize="text-sm"
                            />
                          </div>
                        ) : (
                          <div className="absolute inset-0 flex flex-row items-center justify-between px-2">
                            <div
                              className="absolute p-10 inset-10 flex items-center justify-center bg-gray-200 rounded-full"
                              onClick={() =>
                                fileInputs.current[user.id]?.click()
                              }
                            ></div>
                            <div>
                              <div
                                className="ml-40 w-40 md:w-40 h-20 mt-2 border-2 border-dashed rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-500 text-sm  text-gray-500 hover:text-blue-500 transition-colors"
                                onClick={() =>
                                  fileInputs.current[user.id]?.click()
                                }
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDrop(e, user);
                                }}
                              >
                                <p className="text-center whitespace-pre">
                                  {messages.image.drag_and_drop}
                                </p>
                                <input
                                  type="file"
                                  accept=".png,.jpg,.jpeg"
                                  onChange={(e) => handleFileChange(user.id, e)}
                                  ref={(el) =>
                                    (fileInputs.current[user.id] = el)
                                  }
                                  style={{ display: "none" }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {previewsMap[user.id]?.[0] ? (
                          <div className="flex flex-col justify-center items-center">
                            <img
                              src={previewsMap[user.id][0]}
                              onLoad={() => setImageLoading(false)}
                              className="ml-10 w-24 h-24 rounded-full object-cover"
                            />
                            <div className="pl-10 text-center text-sm text-gray-500 whitespace-nowrap">
                              {user.last_name} {user.first_name}
                            </div>
                          </div>
                        ) : user.profile_image_url ? (
                          <div className="flex flex-col justify-center items-center">
                            <img
                              src={user.profile_image_url}
                              onLoad={() => setImageLoading(false)}
                              className="ml-10 w-24 h-24 rounded-full object-cover"
                            />
                            <div className="pl-10 text-center text-sm text-gray-500 whitespace-nowrap">
                              {user.last_name} {user.first_name}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col justify-center items-center">
                            <svg
                              className="ml-10 w-24 h-24"
                              viewBox="0 0 100 100"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                width="100"
                                height="100"
                                rx="50"
                                fill="#D9D9D9"
                              />
                              <path
                                d="M50 50.375C42.373 50.375 36.25 44.252 36.25 36.625C36.25 29.1055 42.373 22.875 50 22.875C57.5195 22.875 63.75 29.1055 63.75 36.625C63.75 44.252 57.5195 50.375 50 50.375ZM55.3711 55.5312C65.6836 55.5312 74.0625 63.9102 74.0625 74.2227C74.0625 76.2637 72.3438 77.875 70.3027 77.875H29.5898C27.5488 77.875 25.9375 76.2637 25.9375 74.2227C25.9375 63.9102 34.209 55.5312 44.5215 55.5312H55.3711Z"
                                fill="#8E8E8E"
                              />
                            </svg>
                            <div className="pl-10 text-center text-sm text-gray-500 whitespace-nowrap">
                              {user.last_name} {user.first_name}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 text-xl text-gray-900 min-w-[10px]">
                      {user.last_name} {user.first_name}
                    </td>
                    <td className="py-4 text-xl text-gray-900 min-w-[10px]">
                      {user.login_id}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <WarningModal
          title={messages.image.invalid_file_type_title}
          body={messages.image.invalid_file_type}
          confirmText={""}
          cancelText={"閉じる"}
          isOpen={warningModalOpen}
          onRequestClose={function (): void {
            setWarningModalOpen(false);
          }}
        ></WarningModal>
      </div>
    </div>
  );
};

export default UserImageTable;
